import React, { useEffect, useState } from 'react'

import { useTranslation } from '@contexts/translation'
import {
  useForm,
  Control,
  UseFormSetError,
  UseFormSetValue,
  UseFormGetValues,
  UseFormWatch,
  FieldErrors
} from 'react-hook-form'
import {
  onCreateLease,
  onCreateLeaseArticle,
  onDeleteLeaseArticle,
  onUpdateLease
} from '@api/lease'
import { responseHandler } from '@utils/responseHandler'
import { useSnackbar } from 'notistack'
import { format, parse } from 'date-fns'
import { useFetchData } from '@api/fetchData'
import API_URL from '@config/services'
// import debounce from 'lodash.debounce'

import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography
} from '@mui/material'

import RentAndExpensesStep from './rent-expenses/RentAndExpensesStep'
import DiscountsStep from './discounts/DiscountsStep'
import IndexationStep from './indexation/IndexationStep'
import { mutate } from 'swr'
import InvoicingRulesStep from './invoicing-rules/InvoicingRulesStep'
import queryString from '@utils/queryString'
import GeneralInfoStep from './general-information/GeneralInfoStep'
import DocumentsStep from './documents/DocumentsStep'
import { onCreateDiscount } from '@api/discounts'
import { Close } from '@mui/icons-material'
import numberWithCommas from '@utils/numberWithCommas'

interface LeaseFormProps {
  open: boolean
  title: string
  onClose: () => void
  onCloseTooltip?: string
  operationId: string | number
  leaseId?: string | number | null
  fetchLeasesURL: string
  handleOpenLeaseDetails?: (id: string | number) => void
  isEdit: boolean
  handleClose?: () => void
  currency?: string
}

interface LeaseData {
  tenant?: {
    id: string | number
  }
  start_date?: string | Date
  end_date?: string | Date
  deposit?: number
  deposit_months?: number
  guarantee?: number
  guarantee_months?: number
  is_indexed?: boolean
  index_type?: string
  index_rate?: number
  index_base_year?: string | number
  index_base_trimester?: string | number
  invoicing_method?: string
  invoice_day?: number
  billing_option?: string
  invoicing_frequency?: string
  document_type?: string
  invoice_email_to?: string
  invoice_email_cc?: string
  invoice_email_bcc?: string
  date_signed?: string | Date
  rent_data?: {
    rent_base: number
  }
  [key: string]: any
}

interface LotData {
  id: string | number
  lot: string | number
  [key: string]: any
}

interface ArticleData {
  id: string | number
  lot: string | number
  [key: string]: any
}

interface TenantData {
  id: string | number
  email: string
  [key: string]: any
}

interface PeriodsData {
  results?: any[]
  [key: string]: any
}

interface DiscountsData {
  results?: any[]
  [key: string]: any
}

interface LotsData {
  results?: any[]
  [key: string]: any
}

interface FormValues {
  tenant: string | number
  make_deposit: boolean
  deposit: number
  deposit_months: number
  is_indexed: boolean
  [key: string]: any
}

export default function LeaseForm(props: LeaseFormProps): JSX.Element {
  const {
    open,
    title,
    onClose,
    onCloseTooltip,
    operationId,
    leaseId = null,
    fetchLeasesURL,
    handleOpenLeaseDetails,
    isEdit,
    handleClose,
    currency
  } = props
  const { dictionary, t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [selectedLeaseId, setSelectedLeaseId] = useState<
    string | number | null
  >(leaseId)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedLots, setSelectedLots] = useState<(string | number)[]>([])
  const [articles, setArticles] = useState<ArticleData[]>([])
  const [referenceArticle, setReferenceArticle] = useState<(string | number)[]>(
    []
  )
  const [periods, setPeriods] = useState<any[]>([])
  const [completed, setCompleted] = useState<Record<number, boolean>>({})

  const [discounts, setDiscounts] = useState<any[]>([])
  const [discountTitle, setDiscountTitle] = useState<string>()
  const [discountAmount, setDiscountAmount] = useState<number>()
  const [discountDescription, setDiscountDescription] = useState<string>()
  const [discountStartDate, setDiscountStartDate] = useState<Date>()
  const [discountEndDate, setDiscountEndDate] = useState<Date>()

  useEffect(() => {
    setSelectedLeaseId(leaseId)
  }, [leaseId])

  const leaseURL = selectedLeaseId
    ? API_URL.LEASES.GET_LEASE(selectedLeaseId)
    : null
  const { data } = useFetchData<LeaseData | null>(leaseURL)

  const fetchTenantsURL =
    API_URL.TENANTS.GET_TENANTS_LIST() +
    '?' +
    queryString({ operations: operationId, page_size: 1000 })
  const { data: tenants } = useFetchData<{ results?: TenantData[] }>(
    fetchTenantsURL
  )

  const fetchArticlesURL = selectedLeaseId
    ? API_URL.LEASES.GET_LEASE_ARTICLES(selectedLeaseId)
    : null
  const { data: articlesData, isLoading: isArticlesLoading } = useFetchData<{
    results?: ArticleData[]
  }>(fetchArticlesURL)
  useEffect(
    () => setArticles(articlesData?.results || []),
    [articlesData, selectedLeaseId]
  )

  const fetchPeriodsURL = selectedLeaseId
    ? API_URL.LEASES.GET_LEASE_PERIODS(selectedLeaseId)
    : null
  const { data: periodsData, isLoading: isPeriodsLoading } =
    useFetchData<PeriodsData>(fetchPeriodsURL)
  useEffect(() => setPeriods(periodsData?.results || []), [periodsData])

  const fetchDiscountsURL = leaseId
    ? API_URL.DISCOUNTS.GET_DISCOUNT_LIST() +
      '?' +
      queryString({ lease: leaseId })
    : null
  const { data: discountsData, isLoading: isDiscountsLoading } =
    useFetchData<DiscountsData>(fetchDiscountsURL)
  useEffect(() => setDiscounts(discountsData?.results || []), [discountsData])

  const fetchLotsURL =
    API_URL.LOTS.GET_LOTS() +
    '?' +
    queryString({ operation: operationId, is_private: true })
  const { data: lotsData, isLoading: isLotsLoading } =
    useFetchData<LotsData>(fetchLotsURL)

  const {
    control,
    setError,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      tenant: data ? data.tenant?.id : '',
      make_deposit: false,
      deposit: 0,
      deposit_months: 0,
      is_indexed: true
    }
  })

  const watchTenant = watch('tenant')
  const watchStartDate = watch('start_date')
  const watchEndDate = watch('end_date')
  const watchMakeDeposit = watch('make_deposit')
  const watchRent = watch('total_monthly_rent')
  const watchIndexation = watch('is_indexed')
  const watchIndexationFields = watch([
    'is_indexed',
    'index_type',
    'index_base_year',
    'index_base_trimester',
    'index_rate'
  ])
  const watchInvoicingRulesFields = watch([
    'invoicing_method',
    'invoicing_frequency',
    'invoice_day',
    'billing_option',
    'document_type'
  ])
  const watchEmailFields = watch([
    'invoice_email_to',
    'invoice_email_cc',
    'invoice_email_bcc'
  ])

  useEffect(() => {
    const tenantSelected = tenants?.results?.find(
      tenant => tenant.id === watchTenant
    )

    if (tenantSelected) {
      setValue('invoice_email_to', tenantSelected.email)
    }
  }, [watchTenant, tenants, setValue])

  useEffect(() => {
    if (data && data.rent_data) {
      setValue(
        'total_monthly_rent',
        numberWithCommas(data.rent_data.rent_base / 12, 2)
      )
    }
  }, [data, setValue])

  useEffect(() => {
    if (isEdit && selectedLeaseId && data) {
      setValue('tenant', data.tenant?.id)

      setValue(
        'total_monthly_rent',
        numberWithCommas(data?.rent_data.rent_base / 12, 2)
      )

      setValue(
        'start_date',
        data.start_date
          ? parse(data.start_date as string, 'dd-MM-yyyy', new Date())
          : data.start_date
      )

      setValue(
        'end_date',
        data.end_date
          ? parse(data.end_date as string, 'dd-MM-yyyy', new Date())
          : data.end_date
      )

      if (data.deposit !== 0) {
        setValue('make_deposit', true)
        setValue('deposit_months', data.deposit_months)
        setValue('deposit', data.deposit)
      }

      if (data.guarantee) {
        setValue('guarantee_months', data.guarantee_months)
        setValue('guarantee', data.guarantee)
      }

      if (data) {
        setValue('is_indexed', data.is_indexed)
        setValue('index_type', data.index_type)
        setValue('index_rate', data.index_rate)
        setValue('index_base_year', data.index_base_year)
        setValue('index_base_trimester', data.index_base_trimester)
      }

      setValue('invoicing_method', data.invoicing_method)
      setValue('invoice_day', data.invoice_day)
      setValue('billing_option', data.billing_option)
      setValue('invoicing_frequency', data.invoicing_frequency)
      setValue('document_type', data.document_type)

      setValue('invoice_email_to', data.invoice_email_to)
      setValue('invoice_email_cc', data.invoice_email_cc)
      setValue('invoice_email_bcc', data.invoice_email_bcc)

      setValue(
        'date_signed',
        data.date_signed
          ? parse(data.date_signed as string, 'dd-MM-yyyy', new Date())
          : data.date_signed
      )
    }
  }, [data, isEdit, leaseId, setValue, selectedLeaseId])

  const handleDepositMonthsChange = (value: number): void => {
    setValue('deposit_months', value)
    setValue(
      'deposit',
      watchRent && data?.rent_data?.rent_base
        ? Math.round(((Number(value) * data?.rent_data.rent_base) / 12) * 100) /
            100
        : 0
    )
  }

  const handleDepositChange = (value: number): void => {
    setValue('deposit', value)
    setValue(
      'deposit_months',
      watchRent && data?.rent_data?.rent_base
        ? Number((value / (data?.rent_data.rent_base / 12)).toFixed(2))
        : 0
    )
  }

  const handleGuaranteeMonthsChange = (value: number): void => {
    setValue('guarantee_months', value)
    setValue(
      'guarantee',
      watchRent && data?.rent_data?.rent_base
        ? Math.round(((Number(value) * data?.rent_data.rent_base) / 12) * 100) /
            100
        : 0
    )
  }

  const handleGuaranteeChange = (value: number): void => {
    setValue('guarantee', value)
    setValue(
      'guarantee_months',
      watchRent && data?.rent_data?.rent_base
        ? Number((value / (data?.rent_data.rent_base / 12)).toFixed(2))
        : 0
    )
  }

  const handlePreviousActiveStep = (): void => {
    setActiveStep(currentStep => currentStep - 1)
  }

  const handleNextActiveStep = async (): Promise<void> => {
    setActiveStep(currentStep => currentStep + 1)
    await mutate(fetchLeasesURL)
    await mutate(leaseURL)
  }

  const onSubmitLeaseArticle = async (
    leaseId: string | number,
    payload: any
  ): Promise<any> => {
    const response = await onCreateLeaseArticle(leaseId, payload)

    responseHandler({
      response,
      callback: async () => {
        await mutate(API_URL.PROJECTS.GET_PROJECT_ONBOARDING(operationId))
        return response
      },
      dictionary,
      msgSuccess: dictionary.lease_article_created,
      snackbar: enqueueSnackbar
    })

    return response
  }

  const handleSelectLots = async (
    leaseId: string | number,
    startDate?: string,
    endDate?: string,
    selectedLots?: (string | number)[]
  ): Promise<void> => {
    setLoading(true)
    const payload: {
      lease_article: (string | number)[]
      start_date?: string
      end_date?: string
      lot?: string | number
    } = {
      lease_article: selectedLots || []
    }
    if (startDate) payload.start_date = startDate
    if (endDate) {
      payload.end_date = endDate
    }

    const promises = payload.lease_article.map(async lot => {
      payload.lot = lot
      if (referenceArticle.indexOf(lot) === -1) {
        const response = await onSubmitLeaseArticle(leaseId, payload)
        return response
      }
      return null
    })

    const createPromise = await Promise.all(promises)
    if (createPromise) {
      const promises = referenceArticle.map(async lot => {
        if (
          payload.lease_article.indexOf(lot) === -1 &&
          articlesData?.results
        ) {
          const leaseArticleId = articlesData.results.filter(
            article => article.lot === lot
          )

          if (leaseArticleId[0]?.id) {
            const response = await onDeleteLeaseArticle(
              leaseId,
              leaseArticleId[0].id
            )
            return response
          }
        }
        return null
      })
      const deletePromise = await Promise.all(promises)
      if (deletePromise) {
        enqueueSnackbar(dictionary.lease_articles_updated, {
          variant: 'success'
        })
        mutate(fetchLotsURL)
        mutate(fetchArticlesURL)
      }
    }

    await mutate(API_URL.LEASES.GET_LEASE(leaseId))
    setLoading(false)
    //   handleClose?.()
  }

  const handleSubmitLease = async (): Promise<void> => {
    setLoading(true)

    const payload = getValues()
    payload.operation = Number(operationId)
    if (payload.start_date) {
      payload.start_date = format(new Date(payload.start_date), 'dd-MM-yyyy')
    }
    if (payload.end_date) {
      payload.end_date = format(new Date(payload.end_date), 'dd-MM-yyyy')
    }
    if (payload.date_signed) {
      payload.date_signed = format(new Date(payload.date_signed), 'dd-MM-yyyy')
    }

    const response = await onCreateLease(payload)

    if (response?.data?.id) {
      handleSelectLots(
        response.data.id,
        payload.start_date,
        payload.end_date,
        selectedLots
      )
    }

    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)

    responseHandler({
      response,
      callback: () => {
        if (response?.data?.id) {
          setSelectedLeaseId(response.data.id)
        }
        setLoading(false)
        handleNextActiveStep()
      },
      callbackError: () => {
        const errors = response?.data?.errors || []
        if (errors.length > 0) {
          errors.forEach((error: any) => {
            setError(error.field as any, {
              message: error.message
            })
          })
        }
        setLoading(false)
      },
      dictionary,
      msgSuccess: dictionary.lease_created,
      snackbar: enqueueSnackbar
    })
  }

  const handleEditLease = async (): Promise<void> => {
    setLoading(true)
    const payload = getValues()
    payload.operation = Number(operationId)
    if (payload.start_date) {
      payload.start_date = format(new Date(payload.start_date), 'dd-MM-yyyy')
    }
    if (payload.end_date) {
      payload.end_date = format(new Date(payload.end_date), 'dd-MM-yyyy')
    }
    if (payload.date_signed) {
      payload.date_signed = format(new Date(payload.date_signed), 'dd-MM-yyyy')
    }
    if (payload.guarantee === '') {
      payload.guarantee = 0
    }
    if (payload.guarantee_months === '') {
      payload.guarantee_months = 0
    }

    if (!selectedLeaseId) return

    const response = await onUpdateLease(selectedLeaseId, payload)

    handleSelectLots(
      selectedLeaseId,
      payload.start_date,
      payload.end_date,
      selectedLots
    )

    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)

    responseHandler({
      response,
      callback: () => {
        if (response?.data?.id) {
          setSelectedLeaseId(response.data.id)
        }
        setLoading(false)
        handleNextActiveStep()
      },
      callbackError: () => {
        const errors = response?.data?.errors || []
        if (errors.length > 0) {
          errors.forEach((error: any) => {
            setError(error.field as any, {
              message: error.message
            })
          })
        }
        setLoading(false)
      },
      dictionary,
      msgSuccess: dictionary.lease_updated,
      snackbar: enqueueSnackbar
    })
  }

  const handleSubmitDiscount = async (): Promise<void> => {
    setLoading(true)
    const payload: {
      lease: string | number | null
      title?: string
      description?: string
      amount?: number
      start_date?: string
      end_date?: string
    } = {
      lease: selectedLeaseId
    }

    if (discountTitle) payload.title = discountTitle
    if (discountDescription) payload.description = discountDescription
    if (discountAmount) payload.amount = discountAmount

    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)

    if (!discountAmount || !selectedLeaseId) {
      setLoading(false)
      handleNextActiveStep()
      return
    }

    if (discountStartDate) {
      payload.start_date = format(new Date(discountStartDate), 'dd-MM-yyyy')
    }
    if (discountEndDate) {
      payload.end_date = format(new Date(discountEndDate), 'dd-MM-yyyy')
    }

    const response = await onCreateDiscount(payload)

    responseHandler({
      response,
      callback: () => {
        setLoading(false)
        if (fetchDiscountsURL) mutate(fetchDiscountsURL)
        if (leaseId) mutate(API_URL.LEASES.GET_LEASE_SCHEDULE(leaseId))
        handleNextActiveStep()
      },
      callbackError: () => {
        const errors = response?.data?.errors || []
        if (errors.length > 0) {
          errors.forEach((error: any) => {
            setError(error.field as any, {
              message: error.message
            })
          })
        }
        setLoading(false)
      },
      dictionary,
      msgSuccess: dictionary.lease_updated,
      snackbar: enqueueSnackbar
    })
  }

  const handleSubmitIndexation = async (): Promise<void> => {
    setLoading(true)
    const [
      is_indexed,
      index_type,
      index_base_year,
      index_base_trimester,
      index_rate
    ] = watchIndexationFields
    const payload = {
      is_indexed: is_indexed,
      index_type,
      index_base_year,
      index_base_trimester,
      index_rate
    }

    if (!selectedLeaseId) return

    const response = await onUpdateLease(selectedLeaseId, payload)

    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)

    responseHandler({
      response,
      callback: () => {
        setLoading(false)
        handleNextActiveStep()
      },
      callbackError: () => {
        const errors = response?.data?.errors || []
        if (errors.length > 0) {
          errors.forEach((error: any) => {
            setError(error.field as any, {
              message: error.message
            })
          })
        }
        setLoading(false)
      },
      dictionary,
      msgSuccess: dictionary.indexation_created,
      snackbar: enqueueSnackbar
    })
  }

  const handleSubmitInvoicingRules = async (): Promise<void> => {
    if (!watchInvoicingRulesFields) {
      handleNextActiveStep()
      return
    }

    setLoading(true)

    const [
      invoicing_method,
      invoicing_frequency,
      invoice_day,
      billing_option,
      document_type
    ] = watchInvoicingRulesFields

    const [invoice_email_to, invoice_email_cc, invoice_email_bcc] =
      watchEmailFields

    const payload = {
      invoice_email_to,
      invoice_email_cc,
      invoice_email_bcc,
      invoicing_method,
      invoicing_frequency,
      invoice_day,
      billing_option,
      document_type
    }

    if (!selectedLeaseId) return

    const response = await onUpdateLease(selectedLeaseId, payload)

    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)

    responseHandler({
      response,
      callback: () => {
        setLoading(false)
        handleNextActiveStep()
      },
      callbackError: () => {
        const errors = response?.data?.errors || []
        if (errors.length > 0) {
          errors.forEach((error: any) => {
            setError(error.field as any, {
              message: error.message
            })
          })
        }
        setLoading(false)
      },
      dictionary,
      msgSuccess: dictionary.invoicing_rules_created,
      snackbar: enqueueSnackbar
    })
  }

  const handleComplete = async (): Promise<void> => {
    await mutate(fetchLeasesURL)
    if (handleOpenLeaseDetails && selectedLeaseId) {
      handleOpenLeaseDetails(selectedLeaseId)
    }
    handleClose?.()
  }

  const getLots = (lots: ArticleData[]): (string | number)[] =>
    lots?.filter(lot => lot.lot).map(lot => lot.lot) || []

  useEffect(() => {
    if (
      articlesData &&
      articlesData.results &&
      articlesData.results.length > 0
    ) {
      setReferenceArticle(getLots(articlesData.results))
      setSelectedLots(getLots(articlesData.results))
    }
  }, [articlesData])

  const stepperTitle = watchIndexation
    ? [
        dictionary.general_information,
        dictionary.rent_and_expenses,
        dictionary.indexation,
        dictionary.discounts,
        dictionary.invoicing_rules,
        dictionary.documents
      ]
    : [
        dictionary.general_information,
        dictionary.rent_and_expenses,
        dictionary.discounts,
        dictionary.invoicing_rules,
        dictionary.documents
      ]

  const handleContentSteps = (step: number): JSX.Element => {
    switch (step) {
      case 0:
        return (
          <GeneralInfoStep
            operationId={operationId}
            leaseId={selectedLeaseId}
            control={control}
            setValue={setValue}
            setError={setError}
            errors={errors}
            defaultValue=""
            fullWidth={true}
            fetchTenantsURL={fetchTenantsURL}
            tenants={tenants}
            setSelectedLeaseId={setSelectedLeaseId}
            selectedLots={selectedLots}
            setSelectedLots={setSelectedLots}
            lotsData={lotsData}
            isLotsLoading={isLotsLoading}
            periods={periods}
            isPeriodsLoading={isPeriodsLoading}
            fetchPeriodsURL={fetchPeriodsURL}
            activeStep={activeStep}
            handleEndDate={(date: Date) => setValue('end_date', date)}
            watchTenant={watchTenant}
          />
        )
      case 1:
        return (
          <RentAndExpensesStep
            operationId={operationId}
            control={control}
            errors={errors}
            leaseId={selectedLeaseId}
            articles={articles}
            leaseURL={leaseURL}
            fetchArticlesURL={fetchArticlesURL}
            fetchLeasesURL={fetchLeasesURL}
            isLoading={isArticlesLoading}
            startDate={watchStartDate}
            endDate={watchEndDate}
            currency={currency}
            watchMakeDeposit={watchMakeDeposit}
            setValue={setValue}
            handleDepositMonthsChange={handleDepositMonthsChange}
            handleGuaranteeMonthsChange={handleGuaranteeMonthsChange}
            handleDepositChange={handleDepositChange}
            handleGuaranteeChange={handleGuaranteeChange}
          />
        )
      case 2:
        return watchIndexation ? (
          <IndexationStep
            control={control}
            errors={errors}
            setValue={setValue}
            indexation={watchIndexation}
            indexationFields={watchIndexationFields}
          />
        ) : (
          <DiscountsStep
            leaseId={selectedLeaseId}
            startDate={watchStartDate}
            endDate={watchEndDate}
            currency={currency}
            discounts={discounts}
            isLoading={isDiscountsLoading}
            fetchDiscountsURL={fetchDiscountsURL}
            discountTitle={discountTitle}
            discountAmount={discountAmount}
            discountDescription={discountDescription}
            setDiscountTitle={setDiscountTitle}
            setDiscountAmount={setDiscountAmount}
            setDiscountDescription={setDiscountDescription}
            setDiscountStartDate={setDiscountStartDate}
            setDiscountEndDate={setDiscountEndDate}
          />
        )
      case 3:
        return watchIndexation ? (
          <DiscountsStep
            leaseId={selectedLeaseId}
            startDate={watchStartDate}
            endDate={watchEndDate}
            currency={currency}
            discounts={discounts}
            isLoading={isDiscountsLoading}
            fetchDiscountsURL={fetchDiscountsURL}
            discountTitle={discountTitle}
            discountAmount={discountAmount}
            discountDescription={discountDescription}
            setDiscountTitle={setDiscountTitle}
            setDiscountAmount={setDiscountAmount}
            setDiscountDescription={setDiscountDescription}
            setDiscountStartDate={setDiscountStartDate}
            setDiscountEndDate={setDiscountEndDate}
          />
        ) : (
          <InvoicingRulesStep
            control={control}
            errors={errors}
            setValue={setValue}
            invoicingRules={watchInvoicingRulesFields}
            emailFields={watchEmailFields}
          />
        )
      case 4:
        return watchIndexation ? (
          <InvoicingRulesStep
            control={control}
            errors={errors}
            setValue={setValue}
            invoicingRules={watchInvoicingRulesFields}
            emailFields={watchEmailFields}
          />
        ) : (
          <DocumentsStep operationId={operationId} leaseId={selectedLeaseId} />
        )
      case 5:
        return (
          <DocumentsStep operationId={operationId} leaseId={selectedLeaseId} />
        )
      default:
        return <></>
    }
  }

  return (
    <Dialog
      open={open}
      scroll={'paper'}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paperScrollPaper': {
          pt: 1,
          maxHeight: 'calc(100% - 60px)'
        }
      }}
    >
      <>
        <Grid container sx={{ p: 2 }}>
          <Grid item xs={11}>
            <Typography variant="h5" gutterBottom>
              {title}
            </Typography>
          </Grid>

          <Grid item xs={1} textAlign="right">
            <Tooltip title={onCloseTooltip || ''}>
              <IconButton aria-label="close" onClick={onClose} size="small">
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <div style={{ padding: 20 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 5, mt: 2, pb: 3 }}
          >
            {stepperTitle.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        <DialogContent sx={{ pb: 4 }}>
          {handleContentSteps(activeStep)}

          <Grid container justifyContent="space-between" mt={3}>
            <Grid item xs={3} textAlign="left">
              <Button
                disabled={
                  activeStep === 0 ||
                  (!selectedLeaseId && activeStep !== (watchIndexation ? 0 : 0))
                }
                variant="outlined"
                color="primary"
                onClick={handlePreviousActiveStep}
                sx={{ minWidth: 120 }}
              >
                {dictionary.back}
              </Button>
            </Grid>
            <Grid item xs={3} textAlign="center">
              {loading && (
                <CircularProgress
                  size={25}
                  thickness={2}
                  sx={{ color: theme => theme.palette.primary.main }}
                />
              )}
            </Grid>

            <Grid item xs={3} textAlign="right">
              {activeStep === (watchIndexation ? 5 : 4) ? (
                <Button
                  disabled={loading}
                  variant="contained"
                  color="primary"
                  onClick={handleComplete}
                  sx={{ minWidth: 120 }}
                >
                  {dictionary.complete}
                </Button>
              ) : (
                <Button
                  disabled={
                    (activeStep === 0 && !watchTenant) ||
                    loading ||
                    (activeStep === 1 &&
                      watchMakeDeposit &&
                      getValues('deposit') <= 0)
                  }
                  variant="contained"
                  color="primary"
                  onClick={
                    selectedLeaseId
                      ? activeStep === 0
                        ? handleEditLease
                        : activeStep === 1
                        ? watchIndexation
                          ? handleSubmitIndexation
                          : handleSubmitDiscount
                        : activeStep === 2
                        ? watchIndexation
                          ? handleSubmitDiscount
                          : handleSubmitInvoicingRules
                        : activeStep === 3 && watchIndexation
                        ? handleSubmitInvoicingRules
                        : handleNextActiveStep
                      : handleSubmitLease
                  }
                  sx={{ minWidth: 120 }}
                >
                  {activeStep === (watchIndexation ? 5 : 4)
                    ? dictionary.complete
                    : dictionary.next}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </>
    </Dialog>
  )
}
